import { useState, useRef, useEffect } from 'react';
import { HiMoon, HiXMark } from 'react-icons/hi2';

const PRESET_MINUTES = [15, 30, 45, 60];

interface SleepTimerProps {
  isActive: boolean;
  remainingFormatted: string;
  onSetTimer: (minutes: number) => void;
  onCancel: () => void;
}

export function SleepTimer({
  isActive,
  remainingFormatted,
  onSetTimer,
  onCancel,
}: SleepTimerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [customMinutes, setCustomMinutes] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close menu on escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleSetCustom = () => {
    const minutes = parseInt(customMinutes, 10);
    if (minutes > 0 && minutes <= 480) { // Max 8 hours
      onSetTimer(minutes);
      setCustomMinutes('');
      setIsOpen(false);
    }
  };

  const handlePresetClick = (minutes: number) => {
    onSetTimer(minutes);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Timer button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isActive ? `Sleep timer: ${remainingFormatted} remaining` : 'Set sleep timer'}
        aria-expanded={isOpen}
        className={`flex items-center justify-center gap-1.5 px-2 h-8
                   transition-colors rounded-full
                   focus:outline-none focus-visible:ring-2
                   focus-visible:ring-[var(--color-primary)]
                   ${isActive
                     ? 'text-[var(--color-primary)] bg-[var(--color-primary)]/10'
                     : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-elevated)]/60'
                   }`}
      >
        <HiMoon size={16} />
        {isActive && (
          <span className="text-[11px] font-medium tabular-nums">
            {remainingFormatted}
          </span>
        )}
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2
                     bg-[var(--color-surface-elevated)] rounded-lg
                     border border-[var(--color-border)]
                     shadow-lg shadow-black/20
                     overflow-hidden z-50
                     min-w-[160px]"
          role="menu"
        >
          {/* Header */}
          <div className="px-3 py-2 border-b border-[var(--color-border)]">
            <span className="text-xs font-semibold text-[var(--color-text-secondary)]">
              Sleep Timer
            </span>
          </div>

          {/* Cancel button (if active) */}
          {isActive && (
            <button
              onClick={() => {
                onCancel();
                setIsOpen(false);
              }}
              className="w-full px-3 py-2 text-left text-sm
                       text-red-500 hover:bg-red-500/10
                       transition-colors flex items-center gap-2"
              role="menuitem"
            >
              <HiXMark size={16} />
              Cancel Timer
            </button>
          )}

          {/* Preset options */}
          <div className="py-1">
            {PRESET_MINUTES.map((minutes) => (
              <button
                key={minutes}
                onClick={() => handlePresetClick(minutes)}
                className="w-full px-3 py-2 text-left text-sm
                         text-[var(--color-text-secondary)]
                         hover:text-[var(--color-text)]
                         hover:bg-[var(--color-surface-elevated)]/80
                         transition-colors"
                role="menuitem"
              >
                {minutes} minutes
              </button>
            ))}
          </div>

          {/* Custom input */}
          <div className="px-3 py-2 border-t border-[var(--color-border)]">
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="480"
                value={customMinutes}
                onChange={(e) => setCustomMinutes(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSetCustom();
                  }
                }}
                placeholder="Custom"
                className="w-16 px-2 py-1 text-sm
                         bg-[var(--color-surface)] border border-[var(--color-border)]
                         rounded text-[var(--color-text)]
                         placeholder:text-[var(--color-text-muted)]
                         focus:outline-none focus:border-[var(--color-primary)]"
              />
              <span className="text-xs text-[var(--color-text-muted)]">min</span>
              <button
                onClick={handleSetCustom}
                disabled={!customMinutes || parseInt(customMinutes, 10) <= 0}
                className="px-2 py-1 text-xs font-medium
                         bg-[var(--color-primary)] text-white rounded
                         hover:bg-[var(--color-primary)]/90
                         disabled:opacity-50 disabled:cursor-not-allowed
                         transition-colors"
              >
                Set
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
